<template>
    <transition name="modal">
        <div class="dialog-modal-mask">
            <div class="small-panel" @click.stop>
                <div class="dialog-title">
                    {{ $t('message.title_link_advanced_settings', {tenantName: targetTenant.tenantName}) }}
                </div>
                <hr class="dialog-hr">
                <div class="dialog-body-small">
                    <el-form>
                        <el-form-item v-for="opt in options" :key="opt.prop">
                            <el-checkbox class="dialog__checkbox" v-model="settings[opt.prop]">
                                <span class="dialog__checkbox-label" v-t="opt.label"></span>
                            </el-checkbox>
                            <div v-if="opt.description_as_html ?? false" class="description"
                                 v-html="$t(opt.description)"/>
                            <div v-else class="description">
                                {{ $t(opt.description, {tenantName: targetTenant.tenantName}) }}
                            </div>
                        </el-form-item>
                    </el-form>
                </div>
                <hr class="dialog-hr">
                <div class="dialog-button-field">
                    <el-button class="dialog-cancel-button" @click="$emit('cancel')" v-t="'message.button_cancel'"></el-button>
                    <el-button class="dialog-ok-button" @click="save" :disabled="!isModified" v-t="'message.button_save'"></el-button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import commonUtil from '@/components/commonutil.js';
import constants from '@/components/constants.js'
import server from "@/components/ServerApi.js";

const originalSettings = {};
const OPTION_MAP = {
    [constants.LINK_CATEGORY.IMPORT]: [
        {
            prop: 'ViewGroupTalkRecord',
            flag: constants.C2C_OPTION.VIEW_GROUP_TALK_RECORD,
            label: 'message.item_c2c_option_view_group_talk_record',
            description: 'message.item_c2c_option_view_group_talk_record_description',
        },
        {
            prop: 'AllowImportTenantToViewUserTracking',
            flag: constants.C2C_OPTION.ALLOW_IMPORT_TENANT_TO_VIEW_USER_TRACKING,
            label: 'message.item_c2c_option_view_group_user_tracking',
            description: 'message.item_c2c_option_view_group_user_tracking_description',
        },
    ],
    [constants.LINK_CATEGORY.EXPORT]: [
        {
            prop: 'SkipAclCheck',
            flag: constants.C2C_OPTION.SKIP_ACL_CHECK,
            label: 'message.item_c2c_option_skip_acl_check',
            description: 'message.item_c2c_option_skip_acl_check_description',
        },
        {
            prop: 'AllowCall',
            flag: constants.C2C_OPTION.ALLOW_CALL,
            label: 'message.item_c2c_option_call',
            description: 'message.item_c2c_option_call_description',
            description_as_html: true,
        },
    ],
}

export default {
    name: "LinkTenantSettingAdvancedSettings",
    props: {
        targetTenant: {type: Object, required: true},
        linkCategory: {type: Number, required: true},
    },
    data() {
        const settings = {};
        for (const [, settingList] of Object.entries(OPTION_MAP)) {
            settingList.forEach(({prop}) => settings[prop] = false);
        }

        return {
            settings,
        };
    },
    computed: {
        options() {
            return OPTION_MAP[this.linkCategory] || [];
        },
        isModified() {
            return this.options.some(({prop}) =>
                this.settings[prop] !== originalSettings[prop]
            );
        },
    },
    async created() {
        if (this.linkCategory === constants.LINK_CATEGORY.EXPORT) {
            await this.fetchExportTenantAdvancedSettings();
        } else if (this.linkCategory === constants.LINK_CATEGORY.IMPORT) {
            await this.fetchImportTenantAdvancedSettings();
        }
    },
    methods: {
        async fetchImportTenantAdvancedSettings() {
            if (this.linkCategory !== constants.LINK_CATEGORY.IMPORT) {
                console.error("the link category is invalid");
                return;
            }

            const payload = {
                linkTenantId: this.targetTenant.tenantId,
            };
            try {
                const res = await server.post("/getimporttenantc2coption", payload);
                this.handleResponse(res, "fetchImportTenantAdvancedSettings");
            } catch (e) {
                commonUtil.putExceptionErrorMessage(this, "fetchImportTenantAdvancedSettings", e);
            }
        },
        async fetchExportTenantAdvancedSettings() {
            if (this.linkCategory !== constants.LINK_CATEGORY.EXPORT) {
                console.error("the link category is invalid");
                return;
            }

            const payload = {
                linkTenantId: this.targetTenant.tenantId,
            };
            try {
                const res = await server.post("/getexporttenantc2coption", payload);
                this.handleResponse(res, "fetchExportTenantAdvancedSettings");
            } catch (e) {
                commonUtil.putExceptionErrorMessage(this, "fetchExportTenantAdvancedSettings", e);
            }
        },
        handleResponse(response, functionName) {
            switch (response.data.status) {
                case constants.STATUS_SUCCESS:
                    const flags = response.data.c2cOption || 0;
                    this.options.forEach(({prop, flag}) => {
                        this.settings[prop] = Boolean(flags & flag)
                        originalSettings[prop] = Boolean(flags & flag)
                    });
                    break;
                case constants.STATUS_ERROR_DENIED:
                    commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_cannot_disconnect_link_denied"));
                    break;
                case constants.STATUS_ERROR_NOT_FOUND:
                    commonUtil.notifyMessage(this, "Warning", commonUtil.getMessageText(this.$i18n, "error_link_already_deleted"));
                    this.getTenantConnectInfo();
                    break;
                case constants.STATUS_ERROR_TENANT_ALREADY_DELETED:
                    commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_tenant_already_deleted"));
                    break;
                default:
                    commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_server_api").replace("{functionName}", `${functionName}`).replace("{code}", response.data.status));
                    break;
            }
        },
        save() {
            // if true, set flag
            const newFlags = this.options.reduce(
                (acc, {prop, flag}) => acc | (this.settings[prop] ? flag : 0),
                0
            );
            this.$emit("save", newFlags);
        },
    },
    // for Vue3 migration
    emits: ["cancel", "save"],
}
</script>

<style scoped>
.dialog__checkbox {
    display: flex;
    align-items: center;
}

.dialog__checkbox-label {
    white-space: pre-line;
}
.description {
    padding-left: 20px;
    font-size: 12px;
    color: gray;
    line-height: 2.0;
}
</style>
